import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { PersonAdd } from '@mui/icons-material';
import AdminPageLayout from '../components/admin/AdminPageLayout';
import { questionnaireLeadsAPI } from '../services/questionnaireService';
import { customerService } from '../services/customerService';

const QuestionnaireLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  // Filters
  const [filters, setFilters] = useState({
    stepName: '',
    days: 7,
    customStartDate: null,
    customEndDate: null
  });

  // Customer creation state
  const [creatingCustomer, setCreatingCustomer] = useState(new Set()); // Track which leads are being processed
  const [createdCustomers, setCreatedCustomers] = useState(new Map()); // Track successfully created customers
  const [customerErrors, setCustomerErrors] = useState(new Map()); // Track errors

  const stepOptions = [
    { value: '', label: 'All Steps' },
    { value: 'quote_questionnaire', label: 'Quote Questionnaire' },
    { value: 'quote_questionnaire_complete', label: 'Quote Complete' },
    { value: 'customer', label: 'Customer Info' },
    { value: 'service', label: 'Service Details' },
    { value: 'property', label: 'Property Details' },
    { value: 'scheduling', label: 'Scheduling' },
    { value: 'payment', label: 'Payment' },
    { value: 'guest', label: 'Guest Info' }
  ];

  const daysOptions = [
    { value: 1, label: 'Last 24 hours' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 0, label: 'Custom range' }
  ];

  const fetchLeads = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        limit: pagination.limit,
        offset: (page - 1) * pagination.limit
      };

      if (filters.stepName) {
        params.stepName = filters.stepName;
      }

      if (filters.days > 0) {
        params.days = filters.days;
      }

      const response = await questionnaireLeadsAPI.getLeads(params);
      
      if (response.success) {
        setLeads(response.data.leads);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          hasMore: response.data.pagination.hasMore
        }));
      } else {
        setError('Failed to fetch leads');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, filters.stepName, filters.days]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handlePageChange = (event, page) => {
    fetchLeads(page);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatFormData = (formData) => {
    if (!formData || typeof formData !== 'object') return 'N/A';
    
    const important = {};
    
    // Name information
    if (formData.name) {
      important.name = formData.name;
    } else if (formData.firstName) {
      important.name = `${formData.firstName} ${formData.lastName || ''}`.trim();
    }
    
    // Contact information
    if (formData.email) important.email = formData.email;
    if (formData.phone) important.phone = formData.phone;
    if (formData.phoneNumber) important.phone = formData.phoneNumber;
    
    // Property information
    if (formData.address) important.address = formData.address;
    if (formData.residenceType) important.propertyType = formData.residenceType;
    if (formData.squareFootage) important.sqft = `${formData.squareFootage} sq ft`;
    if (formData.bedrooms) important.bedrooms = `${formData.bedrooms} bed`;
    if (formData.bathrooms) important.bathrooms = `${formData.bathrooms} bath`;
    
    // Service information
    if (formData.cleaningType) important.service = formData.cleaningType;
    if (formData.frequency) important.frequency = formData.frequency;
    if (formData.serviceType) important.service = formData.serviceType;
    if (formData.cleanByDate) important.cleanBy = formData.cleanByDate;
    if (formData.lastCleaned) important.lastCleaned = formData.lastCleaned;
    if (formData.cleanlinessLevel) important.condition = formData.cleanlinessLevel;
    
    // Other important fields
    if (formData.specialOccasion) important.occasion = formData.specialOccasion;
    if (formData.decisionMaker) important.decision = formData.decisionMaker;
    if (formData.accessInstructions) important.access = formData.accessInstructions;
    
    // Show questionnaire type if available
    if (formData.questionnaire_type) important.type = formData.questionnaire_type;
    if (formData.source) important.source = formData.source;
    
    const formattedData = Object.entries(important)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n') || 'No key data';
      
    return formattedData;
  };

  const getStepChipColor = (stepName) => {
    const colors = {
      'quote_questionnaire': 'primary',
      'quote_questionnaire_complete': 'success',
      'customer': 'info',
      'service': 'secondary',
      'property': 'warning',
      'scheduling': 'info',
      'payment': 'success',
      'guest': 'default'
    };
    return colors[stepName] || 'default';
  };

  // Handle creating customer from lead
  const handleCreateCustomer = async (lead) => {
    const leadId = lead.id;
    
    // Check if already created or in progress
    if (createdCustomers.has(leadId) || creatingCustomer.has(leadId)) {
      return;
    }

    // Clear any previous error
    setCustomerErrors(prev => {
      const newMap = new Map(prev);
      newMap.delete(leadId);
      return newMap;
    });

    // Set loading state
    setCreatingCustomer(prev => new Set([...prev, leadId]));

    try {
      const result = await customerService.createCustomerFromLead(lead);
      
      if (result.success && result.customer) {
        // Mark as successfully created
        setCreatedCustomers(prev => new Map([...prev, [leadId, result.customer]]));
        
        // Show success message
        setError(null);
        // Could add a success toast here if you have a toast system
      } else {
        throw new Error('Failed to create customer - no customer data returned');
      }
    } catch (error) {
      console.error('Error creating customer from lead:', error);
      
      // Store error for this specific lead
      setCustomerErrors(prev => new Map([...prev, [leadId, error.message]]));
      
      // Show general error
      setError(`Failed to create customer: ${error.message}`);
    } finally {
      // Remove loading state
      setCreatingCustomer(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
    }
  };

  // Check if lead has sufficient data for customer creation
  const canCreateCustomer = (lead) => {
    // Need at least a name (from user or formData)
    const hasName = lead.user?.firstName || 
                   lead.formData?.firstName || 
                   lead.formData?.name;
    
    // Should have either email or phone
    const hasContact = lead.user?.email || 
                      lead.user?.phoneNumber ||
                      lead.formData?.email || 
                      lead.formData?.phone ||
                      lead.formData?.phoneNumber;
    
    return hasName && hasContact;
  };

  // Get display name for lead
  const getLeadDisplayName = (lead) => {
    if (lead.user?.firstName) {
      return `${lead.user.firstName} ${lead.user.lastName || ''}`.trim();
    }
    if (lead.formData?.firstName) {
      return `${lead.formData.firstName} ${lead.formData.lastName || ''}`.trim();
    }
    if (lead.formData?.name) {
      return lead.formData.name;
    }
    return 'Unknown';
  };

  if (loading && leads.length === 0) {
    return (
      <AdminPageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminPageLayout>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AdminPageLayout>
          <Typography variant="h4" component="h1" gutterBottom>
            Questionnaire Leads
          </Typography>

          {/* Summary Cards */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <Card sx={{ minWidth: 200 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Leads
                </Typography>
                <Typography variant="h4">
                  {pagination.total}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Customers Created
                </Typography>
                <Typography variant="h4" color="success.main">
                  {createdCustomers.size}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200 }}>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Creation Errors
                </Typography>
                <Typography variant="h4" color="error.main">
                  {customerErrors.size}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Step</InputLabel>
                <Select
                  value={filters.stepName}
                  label="Step"
                  onChange={(e) => handleFilterChange('stepName', e.target.value)}
                >
                  {stepOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={filters.days}
                  label="Time Range"
                  onChange={(e) => handleFilterChange('days', e.target.value)}
                >
                  {daysOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                onClick={() => fetchLeads(1)}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Data Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>User Info</TableCell>
                  <TableCell>Form Data</TableCell>
                  <TableCell>Session ID</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      {format(parseISO(lead.updatedAt), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.stepName}
                        color={getStepChipColor(lead.stepName)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {lead.user ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {lead.user.firstName} {lead.user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.user.email}
                          </Typography>
                          {lead.user.phoneNumber && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {lead.user.phoneNumber}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Anonymous
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography variant="body2" sx={{ 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        fontSize: '0.875rem',
                        lineHeight: 1.4
                      }}>
                        {formatFormData(lead.formData)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {lead.sessionId.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {lead.ipAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} alignItems="center">
                        {/* Create Customer Button */}
                        {canCreateCustomer(lead) ? (
                          <>
                            {createdCustomers.has(lead.id) ? (
                              <Chip
                                label="Customer Created"
                                color="success"
                                size="small"
                                variant="filled"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ) : creatingCustomer.has(lead.id) ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="text.secondary">
                                  Creating...
                                </Typography>
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PersonAdd />}
                                onClick={() => handleCreateCustomer(lead)}
                                sx={{ fontSize: '0.75rem', py: 0.5 }}
                                disabled={!!customerErrors.has(lead.id)}
                              >
                                Create Customer
                              </Button>
                            )}
                            
                            {/* Show error if any */}
                            {customerErrors.has(lead.id) && (
                              <Chip
                                label="Failed"
                                color="error"
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                                title={customerErrors.get(lead.id)}
                              />
                            )}
                          </>
                        ) : (
                          <Chip
                            label="Insufficient Data"
                            color="default"
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                            title="Missing required name or contact information"
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.limit)}
                onChange={handlePageChange}
                disabled={loading}
              />
            </Box>
          )}

          {leads.length === 0 && !loading && (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">
                No leads found for the selected filters
              </Typography>
            </Box>
          )}
      </AdminPageLayout>
    </LocalizationProvider>
  );
};

export default QuestionnaireLeads;